import type { Locale } from '../../i18n/resources';

/** Profile text in each UI locale. Names are romanized and shown as-is. */
type Localized<T> = Record<Locale, T>;

export interface CheetahExpert {
  alias: string;
  name: string;
  title: Localized<string>;
  bio: Localized<string>;
  tags: Localized<string[]>;
  photo?: string;
  /** Image needs the wide-photo crop (e.g. cwangbo's landscape source). */
  photoWideCrop?: boolean;
}

export const CHEETAH_EXPERTS: CheetahExpert[] = [
  {
    alias: 'cwangbo',
    name: 'Wang Bo',
    title: { zh: '亚马逊云科技中国区软件开发总经理', en: 'General Manager, Software Development, AWS China' },
    bio: {
      zh: '王博现任亚马逊云科技中国区的研发总经理，负责领导中国区的研发创新与技术工作。在加入亚马逊之前，拥有超过十年在阿里巴巴和百度的核心产品研发与管理经验，在电商、在线广告和云计算领域积累了丰富的实战经验。',
      en: 'Wang Bo is General Manager of R&D at AWS China, leading R&D innovation and engineering in China. Before Amazon, Wang Bo spent more than ten years leading core product development at Alibaba and Baidu, with deep hands-on experience in e-commerce, online advertising and cloud computing.',
    },
    tags: {
      zh: ['前沿技术驱动业务增长', '产品研发团队高管', 'AIDLC', '大型团队管理经验'],
      en: ['Technology-driven business growth', 'Product R&D executive', 'AIDLC', 'Leading large teams'],
    },
    photo: '/icons/cheetah-experts/cwangbo.png',
    photoWideCrop: true,
  },
  {
    alias: 'fanxl',
    name: 'Xianlong Fan',
    title: { zh: '亚马逊云科技中国区研发总监', en: 'Director of R&D, AWS China' },
    bio: {
      zh: '范先龙现任亚马逊云科技大中华区研发总监，负责中国区数据与人工智能工程团队。在亚马逊工作 20 年，先后参与并领导过亚马逊中国电商平台建设，主导过 Amazon Prime 的全球扩展和亚马逊日本电商核心业务平台。',
      en: 'Xianlong Fan is Director of R&D for AWS Greater China, leading the China data and AI engineering teams. In 20 years at Amazon, Xianlong Fan has built and led Amazon China\'s e-commerce platform, Amazon Prime\'s global expansion and Amazon Japan\'s core e-commerce platform.',
    },
    tags: {
      zh: ['人工智能原生转型', '20年Amazonian经理人', 'AIDLC', '大型团队管理经验'],
      en: ['AI-native transformation', '20-year Amazonian leader', 'AIDLC', 'Leading large teams'],
    },
    photo: '/images/fanxl.jpg',
  },
  {
    alias: 'gawan',
    name: 'Xiaogang Wang',
    title: { zh: '亚马逊云科技首席架构师', en: 'Principal Architect, AWS' },
    bio: {
      zh: '王小刚现任亚马逊云科技首席架构师，Amazon 14 年。前 10 年深耕 Amazon Store 消费级电商平台端到端研发与技术管理，近 4 年主导 AWS 企业级系统架构交付，擅长 0 到 1。',
      en: 'Xiaogang Wang is a Principal Architect at AWS with 14 years at Amazon: 10 years on end-to-end engineering and technical leadership for the Amazon Store consumer e-commerce platform, and the last 4 years leading AWS enterprise system architecture delivery. Xiaogang Wang excels at building from zero to one.',
    },
    tags: {
      zh: ['人工智能原生转型', 'Agentic OS', 'AIDLC', '大型团队管理经验'],
      en: ['AI-native transformation', 'Agentic OS', 'AIDLC', 'Leading large teams'],
    },
    photo: '/images/gawan.jpg',
  },
  {
    alias: 'yanglicn',
    name: 'Yang Li',
    title: { zh: '亚马逊云科技中国区营销技术团队研发总监', en: 'Director of R&D, Marketing Technology, AWS China' },
    bio: {
      zh: '李杨现任亚马逊云科技大中华区营销技术团队研发经理，负责领导 AWS 中国区营销技术平台的研发与 AI-Native 转型工作。当前带领团队负责支撑 AWS 中国区市场营销的全链路技术运营。',
      en: 'Yang Li leads R&D for the AWS Greater China marketing technology team, driving the platform\'s development and its AI-native transformation. The team runs the end-to-end technology behind AWS China marketing.',
    },
    tags: {
      zh: ['智能体驱动研发变革', '技术运维转型+AI原生', 'AIDLC', '大型团队管理经验'],
      en: ['Agent-driven R&D transformation', 'Tech operations + AI-native', 'AIDLC', 'Leading large teams'],
    },
    photo: '/images/yanglicn.jpg',
  },
  {
    alias: 'nwwan',
    name: 'Nan Wang',
    title: { zh: '亚马逊云科技中国区研发总监', en: 'Director of R&D, AWS China' },
    bio: {
      zh: '王楠现任亚马逊云科技中国区软件研发总监，负责领导大中华区域销售管理系统的研发创新与工程交付。自 2019 年加入亚马逊云科技以来，已深耕六年有余，带领一支涵盖软件开发、前端工程和产品管理的多元化技术团队。',
      en: 'Nan Wang is Director of Software R&D at AWS China, leading R&D and engineering delivery for the Greater China sales management systems. Since joining AWS in 2019, Nan Wang has led a diverse team spanning software development, front-end engineering and product management.',
    },
    tags: {
      zh: ['全栈产品研发', '高效能团队建设', 'AIDLC', '大型团队管理经验'],
      en: ['Full-stack product R&D', 'Building high-performing teams', 'AIDLC', 'Leading large teams'],
    },
    photo: '/images/nwwan.jpg',
  },
  {
    alias: 'zhijunz',
    name: 'Zhijun Zhang',
    title: { zh: '亚马逊云科技中国区研发总监', en: 'Director of R&D, AWS China' },
    bio: {
      zh: '张志军拥有逾 20 年系统软件研发经验的资深技术专家。目前主导亚马逊云科技中国区合作伙伴及云市场平台的研发战略。其职业生涯横跨朗讯、甲骨文、IBM 等软硬件巨头。',
      en: 'Zhijun Zhang is a senior technologist with more than 20 years in systems software. Zhijun Zhang leads the R&D strategy for the AWS China partner and cloud marketplace platforms, after a career spanning Lucent, Oracle and IBM.',
    },
    tags: {
      zh: ['云原生架构与AI技术深度融合', '系统开发及工程提效', 'AIDLC', '大型团队管理经验'],
      en: ['Cloud-native architecture with deep AI integration', 'Systems development and engineering efficiency', 'AIDLC', 'Leading large teams'],
    },
    photo: '/images/zhijunz.jpg',
  },
];
